import { z, IssueData, RefinementCtx } from 'zod';
import { TextKey } from '@mews-framework/localization';
import { WallClockDateService } from '@mews-framework/datetime';
import { PaymentCardType } from '@mews/enums';
import { getPaymentCardTypeConfigFromNumber, luhnCheck } from '@mews-framework/payments/cards';
import { CardNumberEventData, CvvEventData } from '@mews-framework/payments/pciProxy';
import { CustomErrorParams, CustomErrorType } from '../CustomErrorType';

export const cvcSchema = z.string().min(3).max(4).regex(/^\d+$/, { message: TextKey.ValidateCVV });

const expandYearToFourDigits = (year: string) => {
    if (year.length === 2) {
        return year < '70' ? `20${year}` : `19${year}`;
    }
    return year;
};

const makeExpiryDateComparable = (month: string, yearTwoOrFourDigits: string) => {
    const year = expandYearToFourDigits(yearTwoOrFourDigits);
    return `${year}-${month}`;
};

const pciProxyCardTypeRefine = (
    val: CardNumberEventData,
    ctx: RefinementCtx,
    supportedCardTypes: PaymentCardType[]
) => {
    if (!val.type) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.ValidateRequired,
            } satisfies CustomErrorParams,
            fatal: true,
        });
        return z.NEVER;
    }

    if (!val.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.ValidatePaymentCard,
            } satisfies CustomErrorParams,
        });
        return z.NEVER;
    }

    const isValidCardType = supportedCardTypes.some(
        (supportedType) => supportedType.toLowerCase() === val.type.toLowerCase()
    );

    if (!isValidCardType) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.ValidatePaymentCardType,
                substitutes: { type: val.type },
            } satisfies CustomErrorParams,
        });
        return z.NEVER;
    }
};

/**
 * @param comparableExpiryDate - Date in YYYY-MM format
 * @param comparableCurrentMonth - Date in YYYY-MM format
 */
export const isExpiryDateExpired = (comparableExpiryDate: string, comparableCurrentMonth: string) => {
    return comparableExpiryDate < comparableCurrentMonth;
};

const expirationIssue: IssueData = {
    code: z.ZodIssueCode.custom,
    params: {
        errorType: CustomErrorType.Other,
        textKey: TextKey.ValidatePaymentCardExpiration,
    } satisfies CustomErrorParams,
    fatal: true,
};

export const expiryDateSchema = z.string().superRefine((inputValue, ctx) => {
    const value = inputValue.replace(/\s/g, '');

    if (!/^[0-9]{2}\/([0-9]{2}|[0-9]{4})$/.test(value)) {
        ctx.addIssue(expirationIssue);
        return z.NEVER;
    }

    const [month, year] = value.split('/');
    if (!month || !year) {
        ctx.addIssue(expirationIssue);
        return z.NEVER;
    }
    if (month < '01' || month > '12') {
        ctx.addIssue(expirationIssue);
        return z.NEVER;
    }
    const today = WallClockDateService.toJavascriptDate(WallClockDateService.today());
    const comparableCurrentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const comparableExpiryDate = makeExpiryDateComparable(month, year);
    if (isExpiryDateExpired(comparableExpiryDate, comparableCurrentMonth)) {
        ctx.addIssue(expirationIssue);
        return z.NEVER;
    }
});

export const pciProxyValue = ({
    textKey,
    metadata,
}: {
    textKey: Mews.TextKey;
    metadata: CardNumberEventData | CvvEventData;
}) =>
    z.string().refine(
        (_fieldValue) => {
            return metadata.valid ?? false;
        },
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey,
            } satisfies CustomErrorParams,
        }
    );

export const pciProxyCardTypeWithMetadata = ({
    supportedCardTypes,
    metadata,
}: {
    supportedCardTypes: PaymentCardType[];
    metadata: CardNumberEventData;
}) => z.string().superRefine((_fieldValue, ctx) => pciProxyCardTypeRefine(metadata, ctx, supportedCardTypes));

export const pciProxyCardType = ({ supportedCardTypes }: { supportedCardTypes: PaymentCardType[] }) =>
    z
        .object({
            length: z.number(),
            type: z.string(),
            valid: z.boolean(),
        })
        .superRefine((val, ctx) => pciProxyCardTypeRefine(val, ctx, supportedCardTypes));

export const cardNumberSchema = z
    .string()
    .regex(/^[\d\s]*$/, { message: TextKey.ValidatePaymentCard })
    .refine(
        (inputValue) => {
            const cardTypeConfig = getPaymentCardTypeConfigFromNumber(inputValue);
            if (!cardTypeConfig) {
                return false;
            }
            if (!cardTypeConfig.length.includes(inputValue.length)) {
                return false;
            }
            if (cardTypeConfig.luhn && !luhnCheck(inputValue)) {
                return false;
            }

            return true;
        },
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.ValidatePaymentCard,
            } satisfies CustomErrorParams,
        }
    );

export const createCardTypeSchema = (supportedCardTypes: PaymentCardType[]) =>
    z.string().refine(
        (inputValue) => {
            const cardTypeConfig = getPaymentCardTypeConfigFromNumber(inputValue);
            if (!cardTypeConfig) {
                return false;
            }
            return supportedCardTypes.includes(cardTypeConfig.paymentCardType);
        },
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.ValidatePaymentCardType,
            } satisfies CustomErrorParams,
        }
    );

export const pciProxyCVV = z
    .object({
        length: z.number(),
        type: z.string().optional(),
        valid: z.boolean(),
    })
    .refine((val) => val.length > 0 && val.valid, {
        params: {
            errorType: CustomErrorType.Other,
            textKey: TextKey.ValidateCVV,
        } satisfies CustomErrorParams,
    });
